/**
 * ConnectedServicesPage
 *
 * Admin view of the connected_services instance registry. Replaces hand-written SQL inserts with a
 * UI for the full lifecycle:
 *   - List registered instances (password-free)
 *   - Add / edit an instance (name + base URL only — login uses the system-user account configured
 *     in utility_settings, so no credentials are entered here)
 *   - Test the connection against streem-backend before saving (the backend also verifies on save:
 *     it normalizes the URL, applies the allowed client/domain policy, and probes the api.-prefixed
 *     host before the URL as entered — an instance that can't be logged into is never saved)
 *   - Remove (soft-delete: active=false, kept for FK references + audit history)
 */

import React, { useState } from "react";
import {
  CheckCircle2,
  Loader2,
  MoreHorizontal,
  Pencil,
  PlugZap,
  Plus,
  RefreshCw,
  Server,
  Trash2,
  XCircle,
} from "lucide-react";

import { AppSidebar } from "../components/app-sidebar";
import { SiteHeader } from "../components/site-header";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import { useToast } from "../lib/use-toast";
import {
  useCreateInstance,
  useDeleteInstance,
  useInstances,
  useTestConnection,
  useUpdateInstance,
  type ConnectedServiceRequest,
  type Instance,
} from "../lib/api-central-mu";

export default function ConnectedServicesPage() {
  const [addOpen, setAddOpen] = useState(false);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <SiteHeader />
        <main className="p-6 lg:p-8 space-y-6">
          <Header />

          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Register the Leucine instances the Migration Utility can read from and write to.
            </div>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="size-4" />
                  Add instance
                </Button>
              </DialogTrigger>
              <ServiceFormDialog mode="create" onDone={() => setAddOpen(false)} />
            </Dialog>
          </div>

          <ServicesTable />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

function Header() {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2">
          <Server className="size-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Connected Services</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Registered Leucine instances the Migration Utility can connect to. Login uses the
          centrally configured system account.
        </p>
      </div>
    </div>
  );
}

// =====================================================================================
// Table
// =====================================================================================

function ServicesTable() {
  const { data: services, isLoading, isError, refetch } = useInstances();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Instances</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Instances</CardTitle>
          <CardDescription>Couldn't load the instance list.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="size-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!services || services.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No instances yet</CardTitle>
          <CardDescription>
            Use "Add instance" to register a Leucine instance the MU can connect to.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/3">Name</TableHead>
              <TableHead>Base URL</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.map((s) => (
              <ServiceRow key={s.id} service={s} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ServiceRow({ service }: { service: Instance }) {
  const [dialog, setDialog] = useState<"edit" | "delete" | null>(null);

  return (
    <>
      <TableRow>
        <TableCell className="font-medium">{service.name}</TableCell>
        <TableCell className="text-sm text-muted-foreground font-mono">{service.baseUrl}</TableCell>
        <TableCell className="text-right">
          {/* modal={false}: menu items open dialogs — a modal menu races the dialog on the
              body pointer-events restore and freezes the page (radix-ui/primitives#1836). */}
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <MoreHorizontal className="size-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => setDialog("edit")}>
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDialog("delete")}
              >
                <Trash2 className="size-4" />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCell>
      </TableRow>

      <Dialog open={dialog === "edit"} onOpenChange={(o) => !o && setDialog(null)}>
        <ServiceFormDialog mode="edit" service={service} onDone={() => setDialog(null)} />
      </Dialog>
      <Dialog open={dialog === "delete"} onOpenChange={(o) => !o && setDialog(null)}>
        <DeleteServiceDialog service={service} onDone={() => setDialog(null)} />
      </Dialog>
    </>
  );
}

// =====================================================================================
// Create / edit form
// =====================================================================================

function ServiceFormDialog({
  mode,
  service,
  onDone,
}: {
  mode: "create" | "edit";
  service?: Instance;
  onDone: () => void;
}) {
  const [name, setName] = useState(service?.name ?? "");
  const [baseUrl, setBaseUrl] = useState(service?.baseUrl ?? "");
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { mutate: create, isPending: creating } = useCreateInstance();
  const { mutate: update, isPending: updating } = useUpdateInstance();
  const { mutate: test, isPending: testing } = useTestConnection();
  const { toast } = useToast();

  const isEdit = mode === "edit";
  const saving = creating || updating;

  const validateForSave = (): boolean => {
    if (!name.trim()) {
      toast({ variant: "destructive", title: "Name is required" });
      return false;
    }
    if (!baseUrl.trim()) {
      toast({ variant: "destructive", title: "Base URL is required" });
      return false;
    }
    return true;
  };

  const buildBody = (): ConnectedServiceRequest => ({
    name: name.trim(),
    baseUrl: baseUrl.trim(),
  });

  const onTest = () => {
    setTestResult(null);
    test(
      {
        id: service?.id,
        name: name.trim() || undefined,
        baseUrl: baseUrl.trim() || undefined,
      },
      {
        onSuccess: (res) => setTestResult(res),
      }
    );
  };

  const onSave = () => {
    if (!validateForSave()) return;
    if (isEdit && service) {
      update(
        { id: service.id, body: buildBody() },
        {
          onSuccess: () => {
            toast({ title: "Instance updated", description: `${name.trim()} saved.` });
            onDone();
          },
        }
      );
    } else {
      create(buildBody(), {
        onSuccess: (s) => {
          toast({ title: "Instance added", description: `${s.name} registered.` });
          onDone();
        },
      });
    }
  };

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>{isEdit ? "Edit instance" : "Add instance"}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? "Update the instance details. The connection is verified before saving."
            : "Register a Leucine instance. The MU logs in with the centrally configured system account and verifies the connection before saving."}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="cs-name">Name</Label>
          <Input
            id="cs-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. instance name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cs-url">Base URL</Label>
          <Input
            id="cs-url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="base url"
          />

        </div>

        {testResult ? (
          <div
            className={
              testResult.success
                ? "rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm dark:bg-emerald-950/30"
                : "rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
            }
          >
            <div
              className={
                testResult.success
                  ? "flex items-center gap-2 text-emerald-600"
                  : "flex items-center gap-2 text-destructive"
              }
            >
              {testResult.success ? (
                <CheckCircle2 className="size-4" />
              ) : (
                <XCircle className="size-4" />
              )}
              <span className="font-medium">
                {testResult.success ? "Connection successful" : "Connection failed"}
              </span>
            </div>
            {!testResult.success && testResult.message ? (
              <p className="mt-1 text-muted-foreground break-words">{testResult.message}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <DialogFooter className="sm:justify-between">
        <Button variant="outline" onClick={onTest} disabled={testing}>
          {testing ? <Loader2 className="size-4 animate-spin" /> : <PlugZap className="size-4" />}
          Test connection
        </Button>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {isEdit ? "Save" : "Add instance"}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
}

// =====================================================================================
// Delete (soft)
// =====================================================================================

function DeleteServiceDialog({ service, onDone }: { service: Instance; onDone: () => void }) {
  const { mutate, isPending } = useDeleteInstance();
  const { toast } = useToast();

  const submit = () => {
    mutate(service.id, {
      onSuccess: () => {
        toast({ title: "Instance removed", description: `${service.name} is no longer active.` });
        onDone();
      },
    });
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Remove instance</DialogTitle>
        <DialogDescription>
          {service.name} will be deactivated and hidden from the list. Existing workflows and audit
          history that reference it are kept; it can be re-enabled from the database later.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={submit} disabled={isPending}>
          {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
          Remove
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
